import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from "recharts";

const money = (v) => (v == null ? "—" : `US$${Number(v).toLocaleString()}`);

const COMPS = [
  { key: "origin", name: "Origin pickup", color: "#0b3d59" },
  { key: "ocean", name: "Ocean freight", color: "#1c7293" },
  { key: "baf", name: "BAF", color: "#e0a458" },
  { key: "ebs", name: "EBS", color: "#c5372f" },
  { key: "pss", name: "PSS", color: "#7c9c3f" },
];

const BUILD = [
  { lane: "Thailand off-peak", origin: 0, ocean: 895, baf: 780, ebs: 238, pss: 0, total: 1913 },
  { lane: "Thailand peak", origin: 0, ocean: 895, baf: 780, ebs: 238, pss: 600, total: 2513 },
  { lane: "UK 40RH", origin: 1134, ocean: 900, baf: 2308, ebs: 0, pss: 0, total: 4342 },
];

function BuildTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <div className="font-semibold text-slate-900 mb-1.5">{label}</div>
      {COMPS.map((c) => (
        row[c.key] > 0 ? (
          <div key={c.key} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.name}</span>
            <span className="font-num text-slate-700">{money(row[c.key])}</span>
          </div>
        ) : null
      ))}
      <div className="flex items-center justify-between gap-4 border-t border-slate-100 mt-1.5 pt-1.5 font-semibold">
        <span>Total</span><span className="font-num">{money(row.total)}</span>
      </div>
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-1.5 text-sm text-slate-600">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2"><span className="text-orange-500 mt-0.5">▸</span><span>{t}</span></li>
      ))}
    </ul>
  );
}

function ContractTable({ head, rows, foot }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-slate-500 text-left">
            {head.map((h, i) => (
              <th key={i} className={`px-3 py-2 font-semibold ${i > 0 ? "text-right font-num" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-slate-100">
              {r.map((c, ci) => (
                <td key={ci} className={`px-3 py-2 ${ci > 0 ? "text-right font-num text-slate-700" : "text-slate-600"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
        {foot && (
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-900">
              {foot.map((c, ci) => (
                <td key={ci} className={`px-3 py-2 ${ci > 0 ? "text-right font-num" : ""}`}>{c}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default function ExecBoardSummary() {
  return (
    <div className="space-y-6" data-testid="budget-board">
      {/* Headline */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="font-num text-xs uppercase tracking-[0.16em] text-orange-600">Executive-board summary · April 2027 – March 2028</div>
        <h3 className="font-display text-xl font-bold text-slate-900 mt-1 mb-3">Headline assumptions</h3>
        <Bullets items={[
          <><span className="font-semibold text-slate-800">Thailand → Australia:</span> forecast NAC averages ~US$2,310 per period-equivalent unit, ranging from US$1,913 off-peak to US$2,513 during peak season.</>,
          <><span className="font-semibold text-slate-800">UK → Australia, 40RH:</span> forecast total cost is ~US$4,342 per FEU, including pickup/origin charges.</>,
          "Thailand's exposure is mainly seasonal PSS and EBS volatility; the UK lane's main exposure is the exceptionally high BAF.",
          "The UK cost is ~US$2,030 higher, or ~88% above Thailand as currently presented.",
          <span className="text-amber-700"><span className="font-semibold">Caveat:</span> the comparison is not yet fully like-for-like — the Thailand source previously describes rates as per TEU while the chart says per FEU. Confirm this unit basis before presenting the percentage comparison to the board.</span>,
        ]} />
      </div>

      {/* Two lanes */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Thailand */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4">
          <h3 className="font-display text-lg font-bold text-slate-900">Thailand → Australia</h3>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Proposed contract assumptions</div>
            <ContractTable
              head={["Cost component", "Apr–Jun 27", "Jul 27–Feb 28", "Mar 28"]}
              rows={[
                ["Ocean freight", "$895", "$895", "$895"],
                ["BAF", "$780", "$780", "$780"],
                ["EBS", "$238", "$238", "$238"],
                ["PSS", "Nil", "$600", "Nil"],
              ]}
              foot={["Total NAC", "$1,913", "$2,513", "$1,913"]}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Board insights</div>
            <Bullets items={[
              "Underlying ocean freight rises from US$869 to US$895 (~3%).",
              "BAF reduces from the late-2026 assumption of US$875 to US$780 (~11% lower).",
              "EBS increases from US$200 to US$238 (~19%), adding fuel/geopolitical contingency.",
              "A US$600 PSS is assumed July through February — adding ~31% to the off-peak total.",
              "PSS applies for ~two-thirds of the year, so the forecast annual average is ~US$2,310.",
              "Versus the prior annual average of ~US$2,303, the model is effectively flat YoY — a conservative position where lower BAF is almost fully offset by EBS and the extended PSS.",
            ]} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Recommended sensitivity</div>
            <Bullets items={[
              <><span className="font-semibold text-slate-800">Low case:</span> US$1,675–1,900 (EBS removed, no PSS).</>,
              <><span className="font-semibold text-slate-800">Base case:</span> ~US$2,310 annual average.</>,
              <><span className="font-semibold text-slate-800">Peak case:</span> US$2,513.</>,
              <><span className="font-semibold text-slate-800">Stress case:</span> ~US$2,700–2,900 (BAF rises, PSS &gt; US$600).</>,
            ]} />
          </div>
        </div>

        {/* UK */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4">
          <h3 className="font-display text-lg font-bold text-slate-900">UK → Australia — 40RH</h3>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Proposed contract assumptions</div>
            <ContractTable
              head={["Component", "Apr 2027 – Mar 2028"]}
              rows={[
                ["Origin pickup", "$1,134"],
                ["Ocean freight", "$900"],
                ["BAF", "$2,308"],
                ["Ocean freight + BAF", "$3,208"],
              ]}
              foot={["Total incl. origin", "$4,342 / FEU"]}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Board insights</div>
            <Bullets items={[
              "Ocean freight reduces from US$1,060 to US$900 (~15% lower).",
              "BAF remains at US$2,308 — more than 2.5× the underlying ocean freight.",
              "BAF is ~72% of the ocean-freight-plus-BAF amount.",
              "Including pickup, BAF still represents ~53% of total forecast spend.",
              "Origin pickup remains fixed at US$1,134 (~26% of the total).",
              "Overall forecast falls modestly from ~US$4,502 to US$4,342 (~3.6% lower).",
              "No PSS or EBS provision is included — a potential budget exposure if carriers introduce exceptional surcharges mid-contract.",
            ]} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Recommended sensitivity</div>
            <Bullets items={[
              <><span className="font-semibold text-slate-800">Base case:</span> US$4,342 / 40RH.</>,
              <><span className="font-semibold text-slate-800">Budget case:</span> ~US$4,500 / 40RH.</>,
              <><span className="font-semibold text-slate-800">Stress case:</span> US$4,800–5,000 / 40RH (BAF +10–15% or an emergency surcharge).</>,
            ]} />
          </div>
        </div>
      </div>

      {/* Conclusions */}
      <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-5 sm:p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-3">Executive conclusions</h3>
        <Bullets items={[
          <><span className="font-semibold text-slate-800">Thailand:</span> budget stability is achievable, but only if the US$600 PSS and US$238 EBS assumptions are contractually controlled.</>,
          <><span className="font-semibold text-slate-800">UK:</span> the commercial priority should be negotiating the BAF formula — reducing ocean freight alone has limited impact.</>,
          "Seek a quarterly BAF review, transparent index linkage, and both upward and downward adjustment.",
          "PSS should have a defined start and end date — it should not remain payable \u201cuntil further notice.\u201d",
          "EBS should be payable only following a documented exceptional event and should auto-expire when the event ends.",
          "Do not allow BAF and EBS to recover the same fuel-cost increase twice.",
          <><span className="font-semibold text-slate-800">For board budgeting,</span> use ~US$2,310 for Thailand and ~US$4,500 for UK 40RH, subject to confirming the Thailand unit basis.</>,
        ]} />
      </div>

      {/* Comparison chart */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
        <h3 className="font-display font-semibold text-slate-900">2027–28 forecast cost build-up</h3>
        <p className="text-xs text-slate-500 mb-4">US$ per stated shipment unit. Thailand peak includes US$600 PSS; UK includes US$1,134 origin pickup. Confirm TEU vs FEU basis for Thailand before board comparison.</p>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={BUILD} margin={{ top: 20, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="lane" tick={{ fill: "#334155", fontSize: 12 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
            <Tooltip content={<BuildTooltip />} cursor={{ fill: "#f8fafc" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {COMPS.map((c, i) => (
              <Bar key={c.key} dataKey={c.key} name={c.name} stackId="1" fill={c.color}
                radius={i === COMPS.length - 1 ? [4, 4, 0, 0] : 0}>
                {i === COMPS.length - 1 && <LabelList dataKey="total" position="top" formatter={(v) => money(v)} style={{ fontSize: 11, fill: "#0f172a", fontWeight: 600 }} />}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
